import React, { useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
} from '@mui/material';
import AnimatedElement from './AnimatedElement';

const HausSection = () => {
  const hausSliderRef = useRef(null);

  // Haus-Slider-Bilder
  const hausSliderImages = [
    {
      id: 1,
      src: "/haus-slider/bild_4.webp",
      alt: "Corps Guestphalia Haus - Bild 1"
    },
    {
      id: 2,
      src: "/haus-slider/Haus-2.webp",
      alt: "Corps Guestphalia Haus - Bild 2"
    },
    {
      id: 3,
      src: "/haus-slider/Haus-3.webp",
      alt: "Corps Guestphalia Haus - Bild 3"
    },
    {
      id: 4,
      src: "/haus-slider/Haus-4.webp",
      alt: "Corps Guestphalia Haus - Bild 4"
    },
    {
      id: 5,
      src: "/haus-slider/Haus-5.webp",
      alt: "Corps Guestphalia Haus - Bild 5"
    }
  ];

  useEffect(() => {
    // Initialisiere Haus Slider
    const initHausSlider = () => {
      if (window.$ && hausSliderRef.current && window.$.fn.slick) {
        try {
          // Prüfe ob Slider bereits initialisiert ist
          if (window.$(hausSliderRef.current).hasClass('slick-initialized')) {
            return;
          }

          // Prüfe ob Slides vorhanden sind
          const slides = hausSliderRef.current.children;
          if (slides.length === 0) {
            return;
          }

          window.$(hausSliderRef.current).slick({
            dots: false,
            infinite: true,
            speed: 500,
            slidesToShow: 1,
            slidesToScroll: 1,
            autoplay: true,
            autoplaySpeed: 4000,
            fade: false,
            cssEase: 'ease-in-out',
            arrows: true,
            appendArrows: '#hausSliderArrows',
            prevArrow: '<button type="button" class="slick-prev custom-arrow custom-prev"><img src="/icon_3_pfeil_zurueck.png" alt="Zurück" /></button>',
            nextArrow: '<button type="button" class="slick-next custom-arrow custom-next"><img src="/icon_2_pfeil_vor.png" alt="Vor" /></button>',
            pauseOnHover: true,
            adaptiveHeight: false
          });
        } catch (error) {
          console.error('Haus slider initialization failed:', error);
        }
      }
    };

    // Initialisiere direkt
    initHausSlider();

    // Cleanup
    return () => {
      if (window.$ && hausSliderRef.current && window.$.fn.slick) {
        try {
          if (window.$(hausSliderRef.current).hasClass('slick-initialized')) {
            window.$(hausSliderRef.current).slick('unslick');
          }
        } catch (error) {
          console.error('Haus slider cleanup failed:', error);
        }
      }
    };
  }, [hausSliderImages]);

  return (
    <Box
      id="haus"
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: 'primary.white'
      }}
    >

      {/* Ganzflächiges Slider ohne Padding */}
      <Container
        maxWidth={false}
        sx={{
           maxWidth: '1400px',
          pl: { xs: 2, md: 14 }, // 80px = 10 * 8px (theme spacing)
          pr: 2,
          mx: 'auto',
          mb: 8
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '3fr 7fr 1fr' },
            gap: 2,
            alignItems: 'start'
          }}
        >

          <AnimatedElement>
          {/* Slider Container */}
          <Box id="hausSlideContainer" sx={{ position: 'relative', display: 'block', margin: { xs: '40px 0', sm: '40px 0', md: '60px 0', xl: '100px 0' },
            }}>
            <Box 
              id="hausSliderArrows" 
              sx={{
                display: 'flex',
                flexDirection: 'row',
                position: 'absolute',
                gap: 0,
                left: { xs: 8, sm: 16, md: -40, xl: -40 },
                top: { xs: -19, sm: -19, md: 80, xl: 80 },
                zIndex: 3,
                '& .custom-arrow': {
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'opacity 0.3s ease',
                  '&:hover': {
                    opacity: 0.7
                  },
                  '& img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }
                }
              }}
            ></Box>
            <Box
              ref={hausSliderRef}
              sx={{
                width: '100%',
                maxWidth: { xs: '100vw', sm: '600px', md: '800px' },
                mx: 'auto',
                px: { xs: 1, sm: 2, md: 0 },
                '& .slick-slide': {
                  outline: 'none',
                  padding: '0',
                  height: { xs: '400px', sm: '500px', md: 'auto' },
                },
                '& .slick-slide > div': {
                  width: '100% !important',
                  height: '100% !important',
                },
                '& .slick-track': {
                  display: 'flex !important',
                  alignItems: 'center',
                  height: { xs: '400px', sm: '500px', md: '600px' },
                },
                '& .slick-slide': {
                  height: 'inherit',
                  '& > div': {
                    height: '100% !important',
                    width: '100% !important',
                  }
                },
                '& .slick-dots': {
                  bottom: '-50px',
                  '& li button:before': {
                    color: '#000',
                    fontSize: '12px',
                  },
                  '& li.slick-active button:before': {
                    color: '#3a7b3e',
                  },
                },
                '& .slick-list': {
                  overflow: 'hidden',
                  borderRadius: '0px',
                  height: { xs: '400px', sm: '500px', md: 'auto' },
                }
              }}
            >
              {hausSliderImages.map((image) => (
                <Box key={image.id} sx={{ height: '100%', width: '100%' }}>
                  <Box
                    component="img"
                    src={image.src}
                    alt={image.alt}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '0px',
                      boxShadow: 'none',
                      display: 'block',
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
          </AnimatedElement>

          <Box></Box>

        </Box>
      </Container>


      <Container
        maxWidth={false}
        sx={{
          maxWidth: '1400px',
          pl: { xs: 2, md: 14 }, // 80px = 10 * 8px (theme spacing)
          pr: 2,
          mx: 'auto',
          mb: 8
        }}
      >
        <AnimatedElement>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr', md: '3fr 7fr 1fr' },
            gap: 2,
            alignItems: 'start'
          }}
        >
          {/* Spalte 1: Sektionsüberschrift */}
          <Box>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2.1rem' },
                fontWeight: 500,
                color: '#000',
                lineHeight: 1.2,
                textAlign: 'left'
              }}
            >
              CORPSHAUS DER GUESTPHALIA BERLIN
            </Typography>
            <Box 
                                      sx={{
                                        height: '4px', 
                                        width: '100px', 
                                        backgroundColor: 'rgb(58, 123, 62)', 
                                        marginTop: { xs: '20px', sm: '30px', md: '40px' },
                                        marginBottom: '20px'
                                      }}
                                    />
          </Box>

          {/* Spalte 2: Zweispaltiger Fließtext */}
          <Box>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.6,
                color: '#000',
                textAlign: 'left',
                columnCount: { xs: 1, md: 2 },
                columnGap: '2rem',
                margin: 0,
                '& p': {
                  breakInside: 'avoid',
                  pageBreakInside: 'avoid', // Fallback
                  marginTop: 0, // Entfernt Abstand über dem ersten Absatz
                  marginBottom: 2 // Abstand zwischen Absätzen
                },
                '& p:first-of-type': {
                  marginTop: 0 // Explizit für den ersten Absatz
                },
                columnRule: 'none',
                marginLeft: { xs: '40px', md: 0 }
              }}
            >
              <p>Das Verbindungshaus in der Schwendenerstraße 13, gelegen in Dahlem im Berliner Bezirk Steglitz-Zehlendorf, ist ein wunderschönes altes Gebäude, das die bewegte Geschichte und Tradition einer der ältesten Studentenverbindungen Berlins verkörpert. Das Haus wurde 1929 erbaut und dient seit 1957 als Heimstätte für das Corps Guestphalia.</p> 
              <p>Das im Villenstil um 1900 errichtete dreigeschossige Gebäude beeindruckt durch seine zurückgenommene aber elegante Architektur. Die verzierte und mit Säulen geschmückte Fassade verleiht dem Haus eine besondere Ausstrahlung Auch im Inneren finden sich zahlreiche historische Details, die von der Geschichte des Corps zeugen. Von seiner besonderen Gemütlichkeit schwärmen die Corpsbrüder bis heute: Vor allem das Kamin- und Altherren-Zimmern werden besonders gern gezeigt und genutzt. </p> 
              <p>Von den insgesamt 15 Räumen werden neun als Aktivenzimmer genutzt. Unser großer Garten lädt nicht nur im Sommer zum Feiern ein. Somit ist unser Corpshaus in der Schwendenerstraße nicht nur ein wunderschönes Gebäude, sondern ein wichtiger Ort für die Mitglieder der Guestphalia. Regelmäßig finden hier Veranstaltungen, wie Kneipen, Vorträge und Feiern statt, die das Zusammengehörigkeitsgefühl der Corpsbrüder stärken.</p>
            </Typography>
          </Box>

          {/* Spalte 3: Gedrehter Text "CORPS" */}
          <Box
            sx={{
               display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              alignItems: 'flex-start',
              height: '100%',
              minHeight: { xs: '100px', md: '400px' }
            }}
          >
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '10px'
            }}>
              <div style={{ height: '100px', width: '2px', backgroundColor: '#3a7b3e', marginLeft: '-4px' }}></div>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '0.8rem', md: '0.9rem' },
                  fontWeight: 500,
                  color: '#000',
                  writingMode: { xs: 'vertical-rl', md: 'vertical-rl' },
                  textOrientation: { md: 'mixed' },
                  whiteSpace: 'nowrap',
                  letterSpacing: '0.2em',
                  textAlign: 'center',
                }}
              >
                HAUS
              </Typography>
            </div>

          </Box>
        </Box>
        </AnimatedElement>
      </Container>
    </Box>
  );
};

export default HausSection;